import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Reveal } from "../lib/motion";
import { RESULTS, DYNO_IMAGES } from "../lib/data";
import { TechnicalImage } from "./TechnicalImage";

const Bar = ({ stock, tuned, label, unit }) => {
  const ref = useRef(null);
  const reducedMotion = useReducedMotion();
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const pctStock = (stock / tuned) * 100;
  const gain = tuned - stock;
  return (
    <div ref={ref}>
      <div className="flex items-end justify-between mb-2">
        <span className="font-mono text-[11px] uppercase tracking-widest text-white/50">{label}</span>
        <span className="font-display font-bold text-white text-lg">{tuned}<span className="text-white/40 text-sm ml-1">{unit}</span></span>
      </div>
      <div className="relative h-2 bg-brand-elevated overflow-hidden">
        <motion.div className="absolute inset-y-0 left-0 bg-slate-600"
          initial={{ width: 0 }} animate={inView ? { width: `${pctStock}%` } : {}} transition={{ duration: reducedMotion ? 0 : 1, ease: "easeOut" }} />
        <motion.div className="absolute inset-y-0 left-0 bg-brand"
          initial={{ width: `${pctStock}%` }} animate={inView ? { width: "100%" } : {}} transition={{ duration: reducedMotion ? 0 : 1.1, delay: reducedMotion ? 0 : 0.5, ease: "easeOut" }}
          style={{ mixBlendMode: "screen" }} />
      </div>
      <div className="flex justify-between mt-1.5">
        <span className="font-mono text-[10px] text-white/40">{`Serie ${stock} ${unit}`}</span>
        <span className="font-mono text-[10px] text-brand">{`+${gain} ${unit}`}</span>
      </div>
    </div>
  );
};

export const Results = () => (
  <section id="ergebnisse" data-testid="results-section" className="relative py-20 md:py-28 lg:py-36 bg-brand-dark border-b border-white/5">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-brand mb-4 flex items-center gap-3">
          <span className="w-8 h-px bg-brand" /> Echte Ergebnisse
        </p>
        <h2 className="section-heading max-w-3xl">
          Leistung, die man spürt
        </h2>
        <p className="mt-5 text-white/60 leading-relaxed max-w-xl">
          {"Zur Verifikation nutzen wir unter anderem einen "}
          <span className="text-white">Straßen-GPS-Dyno</span>, der Leistung und Beschleunigung
          unter realen Bedingungen erfasst.
        </p>
      </Reveal>

      {DYNO_IMAGES.length > 0 && (
        <div className={`dyno-grid grid gap-6 mt-10 md:mt-14 ${DYNO_IMAGES.length > 1 ? "lg:grid-cols-2" : "lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]"}`}>
          {DYNO_IMAGES.map((item, i) => (
            <Reveal key={item.src} className={`min-w-0 ${i === 0 && DYNO_IMAGES.length === 3 ? "lg:col-span-2 lg:w-3/4 lg:mx-auto" : ""}`}>
              <TechnicalImage item={item} />
            </Reveal>
          ))}
          {DYNO_IMAGES.length === 1 && (
            <Reveal className="lg:self-center lg:pl-8">
              <p className="section-eyebrow"><span /> Aus der Praxis</p>
              <h3 className="font-display font-bold text-xl sm:text-2xl leading-snug">Eine Kurve sagt mehr als eine Spitzenzahl.</h3>
              <p className="mt-5 text-white/60 leading-relaxed">
                Das Messprotokoll zeigt den Verlauf von Leistung und Drehmoment über die Drehzahl.
                So lässt sich die Charakteristik einer Abstimmung nachvollziehen.
              </p>
              <p className="mt-4 text-sm text-white/45 leading-relaxed">
                Messbedingungen und Fahrzeugzustand beeinflussen das Ergebnis. Die Originalansicht lässt sich für Details vergrößern.
              </p>
            </Reveal>
          )}
        </div>
      )}

      <Reveal className="mt-14 md:mt-20 border-t border-white/10 pt-8">
        <h3 className="font-display font-bold text-lg text-white">Optimierung im Vergleich</h3>
        <p className="mt-3 text-sm text-white/50 leading-relaxed max-w-2xl">
          Die folgenden Beispielwerte veranschaulichen mögliche Veränderungen. Sie gehören nicht zu den oben gezeigten Messprotokollen und sind keine Leistungszusage.
        </p>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-5 mt-7">
        {RESULTS.map((r, i) => (
          <Reveal key={r.model} delay={i * 0.1}>
            <div className="bg-brand-surface border border-slate-800 p-6 lg:p-8 hover:border-brand/40 transition-colors h-full">
              <div className="mb-8">
                <p className="font-mono text-[10px] uppercase tracking-widest text-brand-light mb-3">Beispielwerte</p>
                <h4 className="font-display font-bold text-lg text-white">{r.model}</h4>
                <p className="font-mono text-xs text-white/40 mt-1">{r.engine}</p>
              </div>
              <div className="space-y-7">
                <Bar stock={r.stockPs} tuned={r.tunedPs} label="Leistung" unit="PS" />
                <Bar stock={r.stockNm} tuned={r.tunedNm} label="Drehmoment" unit="Nm" />
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="font-mono text-[11px] leading-relaxed text-white/40 mt-6">Ergebnisse variieren je nach Fahrzeug, Hardware, Softwarestand und Zustand.</p>
    </div>
  </section>
);
